import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Row, Col } from "react-bootstrap";
import { getRelatedArticlesMeta, preloadArticle } from "../content/articles";
import "../styles/relatedArticles.css";

const MONTHS_HU = [
  "január",
  "február",
  "március",
  "április",
  "május",
  "június",
  "július",
  "augusztus",
  "szeptember",
  "október",
  "november",
  "december",
];

/**
 * Formats an ISO date string ("2026-08-10") as a Hungarian long date
 * ("2026. augusztus 10."), without pulling in a date-formatting dependency.
 *
 * @param {string} isoDate
 * @returns {string}
 */
function formatHungarianDate(isoDate) {
  const [year, month, day] = isoDate.split("-").map(Number);
  return `${year}. ${MONTHS_HU[month - 1]} ${day}.`;
}

/**
 * Card grid of related articles for a given content category — used both on
 * product landing pages (as "related articles for this product") and at the
 * bottom of article pages (as "read more"). Renders nothing when there are
 * no matching articles.
 *
 * @param {{category: string, excludeSlug?: string, limit?: number, title?: string}} props
 */
export default function RelatedArticles({
  category,
  excludeSlug,
  limit = 4,
  title = "Kapcsolódó cikkek",
}) {
  const articles = getRelatedArticlesMeta(category, excludeSlug, limit);
  const slugsKey = articles.map((article) => article.slug).join(",");

  // Prefetch the listed articles once the browser is idle, so a click opens
  // them instantly instead of waiting on the route + body chunk downloads.
  useEffect(() => {
    if (!slugsKey) return undefined;
    const run = () => slugsKey.split(",").forEach(preloadArticle);
    if ("requestIdleCallback" in window) {
      const id = window.requestIdleCallback(run, { timeout: 3000 });
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(run, 1500);
    return () => window.clearTimeout(id);
  }, [slugsKey]);

  if (articles.length === 0) return null;

  return (
    <section className="related-articles">
      <h2 className="h2">{title}</h2>

      <Row className="g-3">
        {articles.map((article) => (
          <Col md={6} lg={3} key={article.slug}>
            <Link
              to={`/${article.slug}`}
              className="related-articles-card card h-100"
              onMouseEnter={() => preloadArticle(article.slug)}
              onFocus={() => preloadArticle(article.slug)}
              onTouchStart={() => preloadArticle(article.slug)}
            >
              <p className="related-articles-title">{article.title}</p>
              <p className="related-articles-excerpt">{article.excerpt}</p>
              <p className="related-articles-date">
                {formatHungarianDate(article.publishedAt)}
              </p>
            </Link>
          </Col>
        ))}
      </Row>
    </section>
  );
}
